import React from 'react';
import { Link } from 'react-router-dom';

export function Hero() {
  return (
    <section className="relative pt-32 pb-20 px-6 text-center overflow-hidden">
      {/* Animated gradient orb */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full blur-[160px] opacity-30"
          style={{
            background: 'radial-gradient(ellipse, #6366f1 0%, #8b5cf6 40%, transparent 70%)',
            animation: 'pulse 6s ease-in-out infinite',
          }}
        />
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.2; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.35; transform: translate(-50%, -50%) scale(1.08); }
        }
        @keyframes waveform {
          0%, 100% { height: 6px; }
          50% { height: 24px; }
        }
        @keyframes typing {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="mx-auto max-w-4xl relative z-10">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 border border-indigo-500/30 bg-indigo-500/10 text-indigo-300 text-xs font-mono px-3 py-1.5 rounded-full mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          Live · AI calling platform
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white leading-[1.05] mb-6">
          AI that makes calls.<br />
          <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">
            So your team doesn't.
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          Deploy AI phone agents that call leads, qualify prospects, and book meetings — 24/7, at scale. From <span className="text-zinc-300 font-medium">$0.09/min</span>. No SDR required.
        </p>

        {/* CTAs */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
          <Link
            to="/register"
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-6 py-3 rounded-lg transition-colors text-base"
          >
            Start free — no credit card →
          </Link>
          <a
            href="#demo"
            className="border border-white/15 hover:border-white/30 text-zinc-300 hover:text-white font-medium px-6 py-3 rounded-lg transition-colors text-base"
          >
            Watch a live call ↓
          </a>
        </div>
        <p className="text-xs text-zinc-600 mb-12 font-mono">50 free minutes · no contracts · cancel anytime</p>

        {/* Social proof strip */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-16 text-xs font-mono text-zinc-500">
          <span>500+ campaigns launched</span>
          <span className="text-zinc-700">·</span>
          <span>2.4M calls made</span>
          <span className="text-zinc-700">·</span>
          <span className="text-yellow-500/80">★★★★★</span><span> 4.8 on Product Hunt</span>
        </div>

        {/* Mock phone call card */}
        <div id="demo" className="mx-auto max-w-lg text-left">
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-6 shadow-[0_0_80px_rgba(99,102,241,0.15)] backdrop-blur-sm">
            {/* Card header */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs text-zinc-500 font-mono mb-0.5">OUTBOUND CALL</div>
                <div className="text-sm font-semibold text-white">Sarah Chen</div>
                <div className="text-xs text-zinc-400">+1 (415) 555-0182 · Lead #4821</div>
              </div>
              <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                <span className="text-xs text-green-400 font-mono">ACTIVE</span>
              </div>
            </div>

            {/* Waveform */}
            <div className="flex items-center gap-[3px] mb-4 h-8">
              {Array.from({ length: 28 }).map((_, i) => (
                <div
                  key={i}
                  className="w-1 rounded-full bg-indigo-500/70"
                  style={{
                    height: `${Math.random() * 20 + 4}px`,
                    animation: `waveform ${0.4 + (i % 5) * 0.15}s ease-in-out infinite`,
                    animationDelay: `${(i * 0.05) % 0.6}s`,
                  }}
                />
              ))}
            </div>

            <div className="border-t border-white/[0.06] mb-4" />

            {/* Transcript */}
            <div className="space-y-3 mb-4 font-mono text-xs">
              <div className="flex gap-2">
                <span className="text-zinc-600 shrink-0 w-10">LEAD</span>
                <span className="text-zinc-300">Hey, I saw your ad about the marketing tool?</span>
              </div>
              <div className="flex gap-2">
                <span className="text-indigo-400 shrink-0 w-10">AI</span>
                <span className="text-zinc-300">Yes! I'm calling from Acme — you filled out a form last week. Got 2 minutes to chat?</span>
              </div>
              <div className="flex gap-2">
                <span className="text-zinc-600 shrink-0 w-10">LEAD</span>
                <span className="text-zinc-300">Sure, what does it do exactly?</span>
              </div>
              <div className="flex gap-2 items-start">
                <span className="text-indigo-400 shrink-0 w-10">AI</span>
                <span className="text-zinc-300 flex items-center gap-1">
                  It automates your outreach at scale —
                  <span className="inline-flex gap-0.5 items-center ml-1">
                    <span className="w-1 h-1 rounded-full bg-indigo-400 animate-pulse" style={{ animationDelay: '0s' }} />
                    <span className="w-1 h-1 rounded-full bg-indigo-400 animate-pulse" style={{ animationDelay: '0.2s' }} />
                    <span className="w-1 h-1 rounded-full bg-indigo-400 animate-pulse" style={{ animationDelay: '0.4s' }} />
                  </span>
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="border-t border-white/[0.06] pt-3 flex items-center gap-4 text-xs text-zinc-500 font-mono">
              <span>01:24</span>
              <span>·</span>
              <span>Intent: <span className="text-yellow-400">Interested</span></span>
              <span>·</span>
              <span>$0.09/min</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
